import { PageBackLink } from "@/components/page-back-link"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import type { DataGridColumn } from "@/components/data-grid/data-grid"
import { useState } from "react"

import { AuditFiltersSchema, type AuditLogEntry, type AuditLogListResponse } from "@optimist/schemas"
import { DataGrid } from "@/components/data-grid"
import { ErrorBanner, ErrorBoundary } from "@/components/error-boundary"
import { PageBreadcrumbs, PageHeader } from "@/components/page-header"
import { Pagination } from "@/components/pagination"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { AdminGuard } from "@/features/admin/components/admin-guard"
import { usePortalAuth } from "@/features/authz/hooks/use-portal-auth"
import { api } from "@/lib/api"
import { getRowIndexProps } from "@/lib/pagination"
import { queryKeys } from "@/lib/query-keys"
import { formatDateTime } from "@/lib/utils"

export const Route = createFileRoute("/dashboard/audit/")({
	component: AuditPage,
	validateSearch: (search) => AuditFiltersSchema.pick({
		page: true,
		limit: true,
		resourceType: true,
		resourceId: true,
	}).parse(search),
})

function formatLabel(value: string | null): string {
	if (value === null || value.length === 0) return "—"
	return value.replaceAll("_", " ").replaceAll(".", " · ")
}

function getActorDisplay(entry: AuditLogEntry): { readonly primary: string; readonly secondary: string | null } {
	const name = [entry.actorFirstName, entry.actorLastName]
		.filter((value): value is string => value !== null && value.trim().length > 0)
		.join(" ")
	if (name.length > 0) return { primary: name, secondary: entry.actorEmail }
	if (entry.actorEmail !== null) return { primary: entry.actorEmail, secondary: formatLabel(entry.actorRoleKey) }
	return { primary: entry.actorSub, secondary: formatLabel(entry.actorRoleKey) }
}

function AuditPage() {
	const navigate = useNavigate({ from: Route.fullPath })
	const [selectedDetails, setSelectedDetails] = useState<AuditLogEntry | null>(null)
	const { page, limit, resourceType, resourceId } = Route.useSearch()
	const authQuery = usePortalAuth()
	const canReadAudit = authQuery.data?.permissions.includes("audit.read") === true
	const filters = {
		page,
		limit,
		...(resourceType === undefined ? {} : { resourceType }),
		...(resourceId === undefined ? {} : { resourceId }),
	}
	const auditQuery = useQuery({
		queryKey: queryKeys.audit.list(filters),
		queryFn: () => api.get<AuditLogListResponse>("/audit", { params: filters }),
		enabled: canReadAudit,
	})
	const entries = auditQuery.data?.data ?? []
	const meta = auditQuery.data?.meta ?? null
	const rowIndexProps = getRowIndexProps({ meta, page, limit, mode: "descending" })
	const errorMessage = auditQuery.error instanceof Error ? auditQuery.error.message : "An unknown error occurred"

	const columnDefs: DataGridColumn<AuditLogEntry>[] = [
		{
			field: "createdAt",
			headerName: "Timestamp",
			render: (row) => typeof row.createdAt === "string" ? formatDateTime(row.createdAt) : "—",
		},
		{
			headerName: "Actor",
			render: (row) => {
				const entry = row
				const actor = getActorDisplay(entry)
				return (
					<div className="min-w-44">
						<p className="font-inter text-data font-semibold max-w-56 truncate text-foreground" title={actor.primary}>{actor.primary}</p>
						{actor.secondary ? <p className="font-inter text-meta font-normal max-w-56 truncate text-muted-foreground" title={actor.secondary}>{actor.secondary}</p> : null}
					</div>
				)
			},
		},
		{
			field: "action",
			headerName: "Action",
			render: (row) => (
				<Badge variant="secondary" className="border-brand-border-strong bg-brand-muted text-brand-foreground">
					{typeof row.action === "string" ? formatLabel(row.action) : "—"}
				</Badge>
			),
		},
		{
			headerName: "Resource",
			render: (row) => {
				const entry = row
				return (
					<div>
						<p className="font-inter text-data font-semibold capitalize text-foreground">{formatLabel(entry.entityType)}</p>
						<p className="font-inter text-meta font-normal max-w-48 truncate font-mono text-muted-foreground" title={entry.entityId ?? undefined}>{entry.entityId ?? "—"}</p>
					</div>
				)
			},
		},
		{
			headerName: "Access",
			render: (row) => {
				const entry = row
				return [entry.accessMode, entry.fieldName].filter((value): value is string => value !== null).join(" · ") || "—"
			},
		},
		{
			field: "metadata",
			headerName: "Details",
			render: (row) => {
				const entry = row
				if (Object.keys(entry.metadata).length === 0) return "—"
				return (
					<Button size="sm"
						type="button"
						variant="outline"
						onClick={(event) => {
							event.stopPropagation()
							setSelectedDetails(entry)
						}}
					>
						View details
					</Button>
				)
			},
		},
	]

	return (
		<AdminGuard permission="audit.read">
			<Dialog open={selectedDetails !== null} onOpenChange={(open) => { if (!open) setSelectedDetails(null) }}>
				<DialogContent className="max-h-[calc(100vh-4rem)] overflow-hidden rounded-2xl border-border p-0 sm:max-w-3xl">
					<DialogHeader className="border-b border-border-subtle px-6 py-5 text-left">
						<DialogTitle>Audit details</DialogTitle>
						<DialogDescription>
							{selectedDetails ? `${formatLabel(selectedDetails.action)} · ${formatLabel(selectedDetails.entityType)}` : "Full audit metadata"}
						</DialogDescription>
					</DialogHeader>
					<div className="overflow-auto px-6 pb-6">
						<pre className="max-h-[65vh] overflow-auto whitespace-pre-wrap break-words rounded-xl border border-border-subtle bg-muted p-4 font-mono text-meta leading-5 text-foreground">
							{selectedDetails ? JSON.stringify(selectedDetails.metadata, null, 2) : ""}
						</pre>
					</div>
				</DialogContent>
			</Dialog>
			<ErrorBoundary
				title="Audit page error"
				fallback={<ErrorBanner title="Audit history unavailable" message="Something went wrong loading this page." />}
			>
				<div className="space-y-6">
					<PageHeader back={<PageBackLink to="/dashboard" label="Back to dashboard" />}
						breadcrumbs={
							<PageBreadcrumbs items={[
								<Link key="dashboard" to="/dashboard">Dashboard</Link>,
								<span key="audit">Audit</span>,
							]} />
						}
						title="Audit"
					/>

					<form
						key={`${resourceType ?? "all"}-${resourceId ?? "all"}`}
						className="rounded-2xl border border-border bg-card shadow-card flex flex-col gap-3 p-4 sm:flex-row sm:items-end"
						onSubmit={(event) => {
							event.preventDefault()
							const formData = new FormData(event.currentTarget)
							const nextResourceType = String(formData.get("resourceType") ?? "").trim()
							const nextResourceId = String(formData.get("resourceId") ?? "").trim()
							navigate({
								search: {
									page: 1,
									limit,
									...(nextResourceType.length === 0 ? {} : { resourceType: nextResourceType }),
									...(nextResourceId.length === 0 ? {} : { resourceId: nextResourceId }),
								},
							})
						}}
					>
						<label className="min-w-0 flex-1">
							<span className="font-inter text-label font-semibold uppercase text-subtle-foreground mb-2 block text-subtle-foreground">Resource type</span>
							<Input name="resourceType" defaultValue={resourceType ?? ""} placeholder="e.g. patient" className="h-10 rounded-xl" />
						</label>
						<label className="min-w-0 flex-1">
							<span className="font-inter text-label font-semibold uppercase text-subtle-foreground mb-2 block text-subtle-foreground">Resource ID</span>
							<Input name="resourceId" defaultValue={resourceId ?? ""} placeholder="Exact resource identifier" className="h-10 rounded-xl" />
						</label>
						<div className="flex gap-2">
							<Button type="submit" >Apply filters</Button>
							{resourceType !== undefined || resourceId !== undefined ? (
								<Button
									type="button"
									variant="outline"
									onClick={() => navigate({ search: { page: 1, limit } })}
								>
									Clear
								</Button>
							) : null}
						</div>
					</form>

					{auditQuery.error ? (
						<ErrorBanner
							title="Error loading audit history"
							message={errorMessage}
							onRetry={() => void auditQuery.refetch()}
						/>
					) : null}

					<DataGrid<AuditLogEntry>
						rowData={entries}
						columnDefs={columnDefs}
						loading={auditQuery.isLoading}
						{...rowIndexProps}
					/>

					{meta ? (
						<Pagination
							page={meta.page}
							limit={meta.limit}
							total={meta.total}
							totalPages={meta.totalPages}
							onPageChange={(nextPage) => navigate({ search: (current) => ({ ...current, page: nextPage, limit }) })}
							onLimitChange={(nextLimit) => navigate({ search: (current) => ({ ...current, page: 1, limit: nextLimit }) })}
						/>
					) : null}
				</div>
			</ErrorBoundary>
		</AdminGuard>
	)
}
